import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { FloatingContactButton } from "@/components/FloatingContactButton";
import { CheckCircle, X, ChevronDown, ChevronUp } from "lucide-react";

const DisqueGratuit = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    managerName: '',
    email: ''
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirection vers le lien de référence
    window.open('https://outilmatpro-disque-universel-elitepro.systeme.io/elite-precision', '_blank');
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const testimonials = [
    {
      text: "J'étais sceptique au début, mais ElitePrecision m'a complètement convaincu. La robustesse et la précision du disque me permettent d'offrir des résultats impeccables à mes clients, tout en économisant du temps et de l'argent.",
      author: "Paul, Poseur de Carrelage à Lille"
    },
    {
      text: "ElitePrecision m'a permis d'accomplir mes projets avec une précision inégalée. Les finitions sont parfaites, même sur des matériaux complexes comme le grès cérame. J'ai enfin trouvé un disque sur lequel je peux compter pour tous mes chantiers.",
      author: "Marc, Artisan Carreleur à Marseille"
    },
    {
      text: "C'est simple, ElitePrecision est le meilleur disque que j'ai utilisé en 10 ans de métier. Il me fait économiser du temps, réduit les pertes de matériaux, offre une précision remarquable, et il tient beaucoup plus longtemps que mes anciens disques.",
      author: "Thomas, Carreleur Dallagiste"
    },
    {
      text: "J'étais hésitant à essayer un nouveau produit, mais dès la première utilisation, j'ai su que ce disque ferait partie de mes outils indispensables. Vitesse, précision, durabilité et efficacité impressionnante : tout y est.",
      author: "Olivier, Carreleur à Nantes"
    },
    {
      text: "Avec ElitePrecision, j'ai enfin trouvé un disque capable de suivre le rythme intense de mes chantiers. Même après plusieurs jours d'utilisation sur du grès cérame pleine masse, il garde une coupe nette et sans éclats. Une vraie valeur sûre.",
      author: "Yassine, Chef d'équipe Carrelage à Lyon"
    }
  ];

  const faqItems = [
    {
      question: "Pourquoi cette offre est-elle gratuite ?",
      answer: "C'est simple : nous croyons que la meilleure manière de prouver la qualité de notre disque ElitePrecision, c'est de vous laisser l'essayer. Aucun engagement. Vous êtes libre de commander, tester, et juger par vous-même. Nous misons sur votre satisfaction pour bâtir une relation de confiance durable."
    },
    {
      question: "Que se passe-t-il après avoir passé commande ?",
      answer: "Après commande, vous recevez une confirmation instantanée et votre colis est expédié sous 5 jours. Les disques ELITE PRECISION arrivent prêts à l'emploi avec un guide pratique. Vous pouvez choisir une livraison directement chez vous ou sur chantier. Notre équipe reste disponible pour vous conseiller. Grâce à notre Double Garantie Sérénité, bénéficiez d'un remplacement gratuit en cas de casse et d'un essai gratuit de 30 jours. Commandez en toute tranquillité et optimisez vos chantiers sans interruptions."
    },
    {
      question: "Est-ce que le disque fonctionne sur tous les types de carrelage ?",
      answer: "Oui, le disque ElitePrecision est spécialement conçu pour les carrelages les plus exigeants, comme le grès cérame pleine masse, les grandes dalles, la faïence dure ou le marbre. Faites l'expérience de coupes plus rapides et plus douces avec moins d'écaillage dû aux segments superthin de jante TURBO."
    },
    {
      question: "Pourquoi les disques Elite Précision sont-ils de meilleure qualité que la norme ?",
      answer: "Nos disques ELITE PRECISION sont testés sur le terrain pour garantir leur qualité et leur efficacité. Vous pourrez comparer directement leurs performances avec celles de vos anciens disques et constater par vous-même les gains significatifs en productivité, en qualité de coupe, et en temps. Non seulement vous obtiendrez une coupe plus rapide et plus nette, mais vous réduirez aussi les coûts liés aux interruptions et aux remplacements fréquents."
    },
    {
      question: "Puis-je garder le disque après l'essai ?",
      answer: "Absolument ! Le disque est à vous, sans engagement. Oui, la qualité des disques ELITE PRECISION justifie pleinement leur prix. Leur conception allemande, leurs segments diamantés de 10 mm, et leur technologie avancée (auto-affûtage, soudure laser) garantissent des coupes nettes, une durabilité exceptionnelle et une polyvalence multi-matériaux. Vous économisez à long terme en réduisant les remplacements et les interruptions."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header avec code promo */}
      <div className="bg-orange-500 text-white text-center py-3">
        <p className="font-bold">Profitez d'un essai gratuit avec le code : BIENVENUE</p>
      </div>

      {/* Hero Section */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Révolutionnez Vos Chantiers :
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Le <span className="text-orange-500">Disque Diamant</span> qui Change Tout !
          </h2>
          
          <div className="bg-orange-500 text-white py-4 px-6 rounded-lg mb-8">
            <h3 className="text-xl font-bold mb-4">Pourquoi ElitePrecision est le Meilleur Choix pour les Carreleurs ?</h3>
          </div>

          {/* Avantages */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="p-6">
              <CardContent className="text-center">
                <h4 className="font-bold text-lg mb-3">Précision de Coupe Chirurgicale</h4>
                <p>Obtenez des finitions impeccables dès la première coupe, même sur les matériaux les plus délicats comme le grès cérame pleine masse et la pierre naturelle.</p>
              </CardContent>
            </Card>
            
            <Card className="p-6">
              <CardContent className="text-center">
                <h4 className="font-bold text-lg mb-3">Vitesse Turbo 1,4 mm d'épaisseur</h4>
                <p>Le disque glisse dans les matériaux comme dans du beurre, doublant votre productivité sur chantier.</p>
              </CardContent>
            </Card>
            
            <Card className="p-6">
              <CardContent className="text-center">
                <h4 className="font-bold text-lg mb-3">Durée de Vie 2 Fois Supérieure</h4>
                <p>Les reliefs latéraux innovants réduisent l'usure et augmentent la résistance, même dans les conditions les plus exigeantes.</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA Button */}
          <Button 
            onClick={handleSubmit}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-bold rounded-lg"
          >
            Recevoir Mon Disque Gratuitement
          </Button>
        </div>
      </section>

      {/* Témoignages */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-orange-500">
            Ce Que Disent Nos Clients Carreleurs Satisfaits
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6">
                <CardContent>
                  <p className="italic mb-4">"{testimonial.text}"</p>
                  <p className="font-bold text-orange-500">{testimonial.author}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section Avant/Après */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-500 text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-center">AVANT</h3>
              <h4 className="text-lg font-bold mb-4">les disques ELITE PRECISION</h4>
              <ul className="space-y-3">
                <li className="flex items-center"><X className="mr-2 text-red-300" /> Travail de faible qualité avec des finitions médiocres</li>
                <li className="flex items-center"><X className="mr-2 text-red-300" /> Non respect des délais</li>
                <li className="flex items-center"><X className="mr-2 text-red-300" /> Mauvaise réputation auprès des clients</li>
                <li className="flex items-center"><X className="mr-2 text-red-300" /> Utilisation d'équipement bas de gamme</li>
                <li className="flex items-center"><X className="mr-2 text-red-300" /> Mauvaise rentabilité sur le long terme</li>
              </ul>
            </div>
            
            <div className="bg-green-500 text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-center">APRÈS</h3>
              <h4 className="text-lg font-bold mb-4">les disques ELITE PRECISION</h4>
              <ul className="space-y-3">
                <li className="flex items-center"><CheckCircle className="mr-2 text-green-300" /> Finition chirurgicale et qualité professionnelle</li>
                <li className="flex items-center"><CheckCircle className="mr-2 text-green-300" /> Respect des délais et gain de temps</li>
                <li className="flex items-center"><CheckCircle className="mr-2 text-green-300" /> Fidélisation de la clientèle</li>
                <li className="flex items-center"><CheckCircle className="mr-2 text-green-300" /> Utilisation d'outils de haute qualité</li>
                <li className="flex items-center"><CheckCircle className="mr-2 text-green-300" /> Augmentation des marges et rentabilité accrue</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire de commande */}
      <section className="py-16 px-4 bg-orange-50">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-8">Votre Offre Découverte</h3>
          <p className="text-center text-lg mb-8">
            🎯 <strong>Recevez gratuitement votre disque ElitePrecision</strong> et testez-le sur vos chantiers sans engagement.
          </p>
          
          <Card className="p-8">
            <CardContent>
              <h4 className="text-xl font-bold text-center mb-6">Réservez Votre Disque Gratuit Maintenant</h4>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  name="companyName"
                  placeholder="Nom de la société"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 text-lg"
                />
                
                <Input
                  type="text"
                  name="managerName"
                  placeholder="Nom du dirigeant"
                  value={formData.managerName}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 text-lg"
                />
                
                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 text-lg"
                />
                
                <Button 
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 text-lg font-bold"
                >
                  Profitez de L'Offre Gratuite
                </Button>
              </form>
              
              <div className="text-center mt-4 space-y-2">
                <p className="text-sm text-gray-600">"Données sécurisées & conformes RGPD"</p>
                <p className="text-sm text-gray-600">Plus de 5000 clients professionnels en France</p>
                <p className="font-bold">Disques livrés dans toute la France, même dans les zones les plus reculées</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* À propos */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-8 text-orange-500">Qui sommes-nous</h3>
          <p className="text-lg leading-relaxed mb-8">
            Chez Outilmatpro, nous sommes une entreprise spécialisée dans la vente de disque diamant et consommable de construction destinée aux professionnels du bâtiment. Forts de notre expérience et de notre expertise, nous avons pour mission de fournir à nos clients des produits de la plus haute qualité, provenant des meilleures marques et usines du marché. Nous savons que chaque projet est unique et que le choix du bon matériel est crucial pour assurer la réussite de vos travaux. C'est pourquoi nous sélectionnons rigoureusement chaque produit pour garantir <strong>performance</strong>, <strong>durabilité</strong> et <strong>fiabilité</strong>.
          </p>
          
          <div className="bg-gray-100 p-6 rounded-lg mb-8">
            <h4 className="font-bold text-lg mb-4">Informations légales</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p><strong>OUTILMATPRO</strong></p>
                <p>SAS au capital de 5 000 €</p>
                <p>SIREN : 797 802 279</p>
                <p>SIRET : 797 802 279 00019</p>
              </div>
              <div>
                <p>7 Rue du Général de Monsabert</p>
                <p>34500 Béziers, France</p>
                <p>Tél : +33 6 61 01 84 89</p>
                <p>Email : contact@outilmatpro.fr</p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <h4 className="font-bold text-xl text-orange-500">+5000</h4>
              <p>professionnels satisfaits partout en France</p>
            </div>
            <div className="text-center">
              <h4 className="font-bold text-xl text-orange-500">Salon Batimat</h4>
              <p>élu meilleur disque diamant de l'année</p>
            </div>
            <div className="text-center">
              <h4 className="font-bold text-xl text-orange-500">Garantie totale</h4>
              <p>satisfait ou remboursé</p>
            </div>
          </div>
          
          <div className="bg-orange-500 text-white p-6 rounded-lg">
            <p className="text-xl font-bold">⏳ Réservé aux 100 premiers professionnels : testez gratuitement notre disque diamant et constatez la différence</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12 text-orange-500">Les questions fréquemment posées !</h3>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50"
                  >
                    <h4 className="font-bold text-lg">{item.question}</h4>
                    {openFaq === index ? <ChevronUp /> : <ChevronDown />}
                  </button>
                  
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-orange-500 text-white text-center">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold mb-4">Avant de partir, saisissez votre disque gratuit</h3>
          <p className="text-xl font-bold mb-8 text-orange-200">(Offre Exclusive)</p>
          
          <Button 
            onClick={handleSubmit}
            className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-4 text-lg font-bold"
          >
            Recevoir Mon Disque Gratuitement Maintenant
          </Button>
          
          <div className="mt-8 space-y-2">
            <p className="text-sm">"Données sécurisées & conformes RGPD"</p>
            <p className="text-sm">"Plus de 5000 clients professionnels en France"</p>
          </div>
        </div>
      </section>

      {/* Footer disclaimer */}
      <footer className="py-8 px-4 bg-gray-100 text-center text-sm text-gray-600">
        <p>
          Ce site ne fait pas partie du site YouTube™️, Google™️, Facebook™️, Google Inc. ou Facebook Inc. 
          De plus, ce site n'est PAS approuvé par YouTube™️, Google™️ ou Facebook™️ en aucune façon. 
          FACEBOOK™️ est une marque de commerce de FACEBOOK, Inc. GOOGLE™️ et YOUTUBE™️ sont des marques de commerce de GOOGLE Inc.
        </p>
      </footer>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/33661018489?text=Salut,%20je%20suis%20interress%C3%A9%20par%20le%20disque%20%20ElitePr%C3%A9cision"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-10 left-6 bg-green-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl shadow-lg hover:bg-green-600 transition-colors z-50"
      >
        📱
      </a>
      <FloatingContactButton />
    </div>
  );
};

export default DisqueGratuit;